import React, { type JSX } from "react";
import { useAdminDashboardLocale } from "../locale/admin-dashboard-locale-context";
import { pickAdminDashboardLocaleText } from "../locale/admin-dashboard-copy";

export type AdminStatusBadgeValue =
  | "ready"
  | "empty"
  | "partial"
  | "stale"
  | "unavailable"
  | "permission_denied"
  | "error"
  | "queued"
  | "processing"
  | "succeeded"
  | "failed"
  | "none"
  | "attention"
  | "warning"
  | "critical";

export type AdminStatusBadgeProps = {
  label?: string;
  value: AdminStatusBadgeValue;
};

type BadgePresentation = {
  background: string;
  borderColor: string;
  color: string;
  label: {
    en: string;
    ko: string;
  };
};

const BADGE_PRESENTATIONS: Record<AdminStatusBadgeValue, BadgePresentation> = {
  ready: {
    background: "rgba(20, 184, 166, 0.12)",
    borderColor: "rgba(13, 148, 136, 0.22)",
    color: "#0f766e",
    label: { en: "Ready", ko: "준비" },
  },
  empty: {
    background: "rgba(148, 163, 184, 0.12)",
    borderColor: "rgba(100, 116, 139, 0.24)",
    color: "#475569",
    label: { en: "Empty", ko: "비어 있음" },
  },
  partial: {
    background: "rgba(245, 158, 11, 0.12)",
    borderColor: "rgba(217, 119, 6, 0.24)",
    color: "#b45309",
    label: { en: "Partial", ko: "부분" },
  },
  stale: {
    background: "rgba(249, 115, 22, 0.12)",
    borderColor: "rgba(234, 88, 12, 0.24)",
    color: "#c2410c",
    label: { en: "Stale", ko: "지연" },
  },
  unavailable: {
    background: "rgba(148, 163, 184, 0.14)",
    borderColor: "rgba(100, 116, 139, 0.3)",
    color: "#475569",
    label: { en: "Unavailable", ko: "사용 불가" },
  },
  permission_denied: {
    background: "rgba(99, 102, 241, 0.12)",
    borderColor: "rgba(79, 70, 229, 0.24)",
    color: "#4338ca",
    label: { en: "Denied", ko: "거부" },
  },
  error: {
    background: "rgba(239, 68, 68, 0.12)",
    borderColor: "rgba(220, 38, 38, 0.24)",
    color: "#b91c1c",
    label: { en: "Error", ko: "오류" },
  },
  queued: {
    background: "rgba(59, 130, 246, 0.12)",
    borderColor: "rgba(37, 99, 235, 0.24)",
    color: "#1d4ed8",
    label: { en: "Queued", ko: "대기" },
  },
  processing: {
    background: "rgba(14, 165, 233, 0.12)",
    borderColor: "rgba(2, 132, 199, 0.24)",
    color: "#0369a1",
    label: { en: "Processing", ko: "처리 중" },
  },
  succeeded: {
    background: "rgba(16, 185, 129, 0.12)",
    borderColor: "rgba(5, 150, 105, 0.24)",
    color: "#047857",
    label: { en: "Succeeded", ko: "성공" },
  },
  failed: {
    background: "rgba(225, 29, 72, 0.12)",
    borderColor: "rgba(190, 24, 93, 0.24)",
    color: "#be123c",
    label: { en: "Failed", ko: "실패" },
  },
  none: {
    background: "rgba(20, 184, 166, 0.12)",
    borderColor: "rgba(13, 148, 136, 0.22)",
    color: "#0f766e",
    label: { en: "None", ko: "없음" },
  },
  attention: {
    background: "rgba(250, 204, 21, 0.16)",
    borderColor: "rgba(202, 138, 4, 0.22)",
    color: "#a16207",
    label: { en: "Attention", ko: "주의" },
  },
  warning: {
    background: "rgba(249, 115, 22, 0.12)",
    borderColor: "rgba(234, 88, 12, 0.24)",
    color: "#c2410c",
    label: { en: "Warning", ko: "경고" },
  },
  critical: {
    background: "rgba(225, 29, 72, 0.12)",
    borderColor: "rgba(190, 24, 93, 0.24)",
    color: "#be123c",
    label: { en: "Critical", ko: "치명" },
  },
};

export function toAdminFreshnessBadgeValue(
  value: "fresh" | "stale" | "partial" | "unavailable",
): AdminStatusBadgeValue {
  if (value === "fresh") {
    return "ready";
  }

  if (value === "stale") {
    return "stale";
  }

  if (value === "partial") {
    return "partial";
  }

  return "unavailable";
}

export function toAdminRiskBadgeValue(
  value: "none" | "attention" | "warning" | "critical",
): AdminStatusBadgeValue {
  return value;
}

export function AdminStatusBadge(props: AdminStatusBadgeProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const presentation = BADGE_PRESENTATIONS[props.value];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "5.25rem",
        padding: "6px 10px",
        borderRadius: "999px",
        background: presentation.background,
        border: `1px solid ${presentation.borderColor}`,
        color: presentation.color,
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {props.label ?? pickAdminDashboardLocaleText(locale, presentation.label)}
    </span>
  );
}
